/**
 * Metadata Transformation Utilities
 *
 * Provider-specific metadata transformations for wizard schema selection.
 * Based on wizard-framework metadataFunctions and initialize.js logic.
 */

export interface MetadataObject {
  name: string;
  label: string;
  type: string;
  fields: MetadataField[];
  [key: string]: any;
}

export interface MetadataField {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  externalId?: boolean;
  relationshipObject?: string;
  [key: string]: any;
}

export interface ProviderMetadata {
  objects: MetadataObject[];
  [key: string]: any;
}

/**
 * Salesforce-specific metadata transformation
 * - Adds Lingk_External_Id__c field to all objects
 * - Expands record types
 * - Alphabetizes objects
 */
export const transformSalesforceMetadata = (metadata: ProviderMetadata): ProviderMetadata => {
  const transformed = {
    ...metadata,
    objects: metadata.objects.map((obj) => ({
      ...obj,
      fields: [
        ...obj.fields,
        // Add Lingk External ID field
        {
          name: 'Lingk_External_Id__c',
          label: 'Lingk External ID',
          type: 'string',
          externalId: true,
          required: false,
        },
      ],
    })),
  };

  // Alphabetize objects
  transformed.objects.sort((a, b) => a.label.localeCompare(b.label));

  return transformed;
};

/**
 * Canvas-specific metadata transformation
 * - Nests hierarchies (e.g., courses -> enrollments)
 * - Alphabetizes
 */
export const transformCanvasMetadata = (metadata: ProviderMetadata): ProviderMetadata => {
  // For now, just alphabetize
  // TODO: Implement hierarchy nesting if needed
  const transformed = { ...metadata };
  transformed.objects.sort((a, b) => a.label.localeCompare(b.label));
  return transformed;
};

/**
 * Blackboard-specific metadata transformation
 * - Recursive alphabetical sorting
 */
export const transformBlackboardMetadata = (metadata: ProviderMetadata): ProviderMetadata => {
  const transformed = { ...metadata };
  transformed.objects.sort((a, b) => a.label.localeCompare(b.label));
  return transformed;
};

/**
 * Default metadata transformation
 * - Simple alphabetical sort by label
 */
export const transformDefaultMetadata = (metadata: ProviderMetadata): ProviderMetadata => {
  const transformed = { ...metadata };
  transformed.objects.sort((a, b) => a.label.localeCompare(b.label));
  return transformed;
};

/**
 * Main metadata transformer
 * Routes to provider-specific transformation
 */
export const transformMetadata = (
  metadata: ProviderMetadata,
  providerType: string
): ProviderMetadata => {
  if (!metadata || !metadata.objects) return metadata;

  switch (providerType.toLowerCase()) {
    case 'salesforce':
      return transformSalesforceMetadata(metadata);
    case 'canvas':
    case 'canvasoauth2':
      return transformCanvasMetadata(metadata);
    case 'blackboard':
      return transformBlackboardMetadata(metadata);
    default:
      return transformDefaultMetadata(metadata);
  }
};
